<template>
  <div>
    <naj-box :title="local.bankruptcy.subRequests.requestApplicantInfo">
      <div>
        <request-applicant :form-model="formModel.requestApplicant"
          :legal-capacity-id="formModel.requestApplicant.legalCapacityId" :audit-confirmations="auditConfirmations"
          :is-view-mode="formModel.mainRequest.isRequestOwner || !isNewMode" :is-new-mode="isNewMode"
          :is-edit-mode="isNewMode" :is-not-completed="isNewMode" :is-gov-type="!isBusiness">
          <template slot="prefix" slot-scope="{}">
            <v-col cols="12" md="6" lg="3">
              <naj-input 
                :type-id="formFieldType.selectbox" 
                :value.sync="formModel.requestApplicant.legalCapacityId"
                :label="local.bankruptcy.subRequests.requestApplicantForm.legalCapacity" 
                :items="allowedLegalCapacities"
                item-value="id" 
                item-text="name" 
                @change="legalCapacityChanged"
                :disabled="true">
              </naj-input>
            </v-col>
          </template>
        </request-applicant>
      </div>
    </naj-box>
    <naj-box :title="local.bankruptcy.subRequests.requestApplicantForm.onBehalfOf">
      <v-row>
        <v-col cols="12" md="6" lg="3">
          <naj-input 
            :type-id="formFieldType.selectbox" 
            :value.sync="formModel.requestApplicant.requestOwnerTypeId"
            :label="local.bankruptcy.subRequests.requestApplicantForm.onBehalfOf" 
            :items="allowedRequestOwnerTypes"
            item-value="id" item-text="name" 
            :validates="`required`"
            :disabled="isViewMode || !isObjectionRequest"
            @change="requestOwnerTypeChanged">
          </naj-input>
        </v-col>
        <v-col v-if="isStakeholder" cols="12" md="6" lg="3">
          <naj-input :type-id="formFieldType.selectbox" :value.sync="formModel.requestApplicant.stakeholderTypeId"
            :label="local.bankruptcy.subRequests.requestApplicantForm.stakeholderType" 
            :items="allowedStakeHolderTypes"
            item-value="id" 
            item-text="name" 
            :validates="`required`"
            :disabled="isViewMode || !isObjectionRequest"
            @change="stakeholderTypeChanged"
          >
          </naj-input>
        </v-col>
      </v-row>
      <v-row v-if="showFirmTable">
        <v-col cols="12">
          <identity-verification-form 
            :key="organizationKey"
            :form-model="organizationData" 
            :person-title="local.bankruptcy.common.theCreditor"
            :view-mode="true" 
            :is-table-data="true">
          </identity-verification-form>
        </v-col>
      </v-row>
    </naj-box>
    <naj-box v-if="showCreditorForm"
      :title="local.bankruptcy.subRequests.requestApplicantForm.debtsDetails">
      <v-row>
        <bankrupt-debts-form :form-model="formModel.requestApplicant.creditor" :loading="false"
          :is-gov-sub-request="true"
          :view-mode="isViewMode"></bankrupt-debts-form>
      </v-row>
    </naj-box>
    <naj-box v-if="showPartnerForm"
      :title="local.bankruptcy.subRequests.requestApplicantForm.partnershipDetails">
      <v-row>
        <v-col cols="12">
          <partner-form
          :is-gov-sub-request="true"
          :form-model="formModel.requestApplicant.partner"
          :loading="false"
          :is-edit-mode="isEditMode"
          ></partner-form>
        </v-col>
      </v-row>
    </naj-box>

    <attorney-form
      v-if="showAttorneyForm && requestType.showAttorneyForm"
      :form-model="formModel.requestApplicant"
      :audit-confirmations="auditConfirmations"
      :is-view-mode="!isNewMode"
      :is-new-mode="isNewMode"
      :is-edit-mode="isEditMode"
      :is-not-completed="true"
      :agent="agent"
      :identity-number="formModel.requestApplicant.identityNumber"
    ></attorney-form>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
const { mapState, mapActions } = createNamespacedHelpers('BankruptcyModule');
import {
  requestOwnerTypeEnum,
  legalCapacitiesEnum,
  stakeholderTypesEnum,
  partyTypeEnum,
  creditorClassificationsEnum,
  subRequestType,
} from '@/modules/bankruptcy/helpers/enum';
import { socialIdTypeEnum } from '@/helpers/enum';
import AttorneyForm from '../request/steps/request-applicant-data/attorney-form.vue';
import RequestApplicant from '../request/steps/basic-data/request-applicant/index.vue';
import IdentityVerificationForm from '@/modules/bankruptcy/components/request/identity-verification-form.vue';
import BankruptDebtsForm from '../request/steps/request-details/bankrupt-debts-tab/bankrupt-debts-form.vue';
import PartnerForm from '../request/steps/request-details/partners-tab/partner-form.vue';

export default {
  name: 'OrganizationSubRequestApplicant',
  inject: ['$validator'],
  components: {
    RequestApplicant,
    AttorneyForm,
    IdentityVerificationForm,
    BankruptDebtsForm,
    PartnerForm,
  },
  props: {
    formModel: {
      type: Object,
      required: true,
    },
    requestType: {
      type: Object,
      required: true,
    },
    isNewMode: {
      type: Boolean,
      required: true,
    },
    isViewMode: {
      type: Boolean,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      stakeholderTypesEnum,
      requestOwnerTypeEnum,
      legalCapacitiesEnum,
      subRequestType,
      partyInfo: {},
      auditConfirmations: {},
      requestParties: {},
      partyTypeEnum,
      creditorClassificationsEnum,
      socialIdTypeEnum,
    };
  },
  computed: {
    ...mapState(['legalCapacities', 'currentUser', 'allRequestOwnerTypes', 'stakeholderTypes']),
    userInfo() {
      return this.currentUser;
    },
    organizationKey() {
      return `${this.formModel.requestApplicant.requestOwnerTypeId}-${this.isDebtorRequestOwner}`;
    },
    allowedLegalCapacities() {
      if(this.userInfo.isBankruptcyTrustee){
        return this.legalCapacities.filter(
          (x) =>
            x.id == legalCapacitiesEnum.bankruptcySecretary,
        ).map((x) => {
          if (x.id == legalCapacitiesEnum.bankruptcySecretary) {
            x.name = this.local.bankruptcy.bankruptcyFile.legalCapacities.bankruptcyCommissionRepresentative;
          }
          return x;
        });
      }
      if (this.isBusiness) {
        if (this.isTrusteeApplying) {
          return this.legalCapacities.filter(
            (x) =>
              x.id == legalCapacitiesEnum.bankruptcySecretary,
          );
        };
        return this.legalCapacities.filter(
          (x) =>
            x.id == legalCapacitiesEnum.legalEntityRepresentative,
        );
      }
      else {
        return this.legalCapacities.filter(
          (x) =>
            x.id == legalCapacitiesEnum.administrativeEntityRepresentative || x.id == legalCapacitiesEnum.bankruptcySecretary,
        ).map((x) => {
          if (x.id == legalCapacitiesEnum.administrativeEntityRepresentative) {
            x.name = this.local.bankruptcy.bankruptcyFile.legalCapacities.governmentalEntityRepresentative;
          }
          if (x.id == legalCapacitiesEnum.bankruptcySecretary) {
            x.name = this.local.bankruptcy.bankruptcyFile.legalCapacities.bankruptcyCommissionRepresentative;
          }
          return x;
        });
      }
    },
    allowedRequestOwnersForGov(){
      if(this.formModel.requestApplicant.legalCapacityId == legalCapacitiesEnum.bankruptcySecretary){
        return this.allRequestOwnerTypes.filter(lc =>
              lc.id == requestOwnerTypeEnum.debtor ||
              lc.id == requestOwnerTypeEnum.bankruptcyCommission
            ).map((x) => {
              if (x.id == requestOwnerTypeEnum.bankruptcyCommission) {
                x.name = this.local.bankruptcy.subRequests.requestInquiry.behalfHimSelf;
              }
              return x;
          }
        );
      }
      return this.allRequestOwnerTypes.filter(lc =>
          lc.id == requestOwnerTypeEnum.stakeholder
      );
    },
    allowedRequestOwnersForBusiness(){
      const isDebtorRepresentative = 
        this.userInfo.nationalRecordNumber === this.formModel.requestApplicant.debtor.unifiedNumber || 
        this.userInfo.commercialRecordNumber === this.formModel.requestApplicant.debtor.commercialRegistrationNumber;

      if(this.isTrusteeApplying){
        return this.allRequestOwnerTypes.filter(lc =>
            (lc.id == requestOwnerTypeEnum.debtor && isDebtorRepresentative) ||
            lc.id == requestOwnerTypeEnum.trustee
          ).map((x) => {
            if (x.id == requestOwnerTypeEnum.trustee) {
              x.name = this.local.bankruptcy.subRequests.requestInquiry.behalfHimSelf;
            }
            return x;
          });
      }
      return this.allRequestOwnerTypes.filter(lc =>
          (lc.id == requestOwnerTypeEnum.debtor && isDebtorRepresentative) ||
          (lc.id == requestOwnerTypeEnum.stakeholder && !isDebtorRepresentative)
      );
    },
    allowedRequestOwnerTypes() {
      if(this.isViewMode){
        return this.allRequestOwnerTypes.map((x) => {
              if (x.id == requestOwnerTypeEnum.bankruptcyCommission || x.id == legalCapacitiesEnum.trustee) {
                x.name = this.local.bankruptcy.subRequests.requestInquiry.behalfHimSelf;
              }
              return x;
          });
      }
      if(this.isBusiness){
        return this.allowedRequestOwnersForBusiness;
      }
      else {
        return this.allowedRequestOwnersForGov;
      }
    },
    allowedStakeHolderTypes() {
      return this.stakeholderTypes.filter(st =>
          st.id == stakeholderTypesEnum.creditor ||
          st.id == stakeholderTypesEnum.partner
        );
    },
    organizationData() {
      if(this.isDebtorRequestOwner){
        return this.formModel.requestApplicant.debtor;
      }
      if(this.isTrusteeRequestOwner && this.isBusiness){
        return {
          ...this.formModel.requestApplicant,
          identityTypeName: socialIdTypeEnum[this.formModel.requestApplicant.identityTypeId],
          unifiedNumber: this.formModel.requestApplicant.identityNumber,
          typeId: 1,
        };
      }
      if (this.isNewMode) {
        return {
          organizationName: this.userInfo.fullName,
          unifiedNumber: this.userInfo.nationalRecordNumber,
          commercialRegistrationNumber: this.userInfo.commercialRecordNumber,
          typeId: 2,
        };
      } else {
        return {
          organizationName: this.formModel.requestApplicant.organizationName,
          unifiedNumber: this.formModel.requestApplicant.unifiedNumber,
          commercialRegistrationNumber: this.formModel.requestApplicant.commercialRegistrationNumber,
          typeId: 2,
        };
      }
    },
    showAttorneyForm() {
      return (!this.isTrusteeApplying && this.isObjectionOnSubRequest && this.isBusiness && this.formModel.requestApplicant.requestOwnerTypeId && this.formModel.requestApplicant.legalCapacityId == legalCapacitiesEnum.legalEntityRepresentative);
    },
    isOrganizationBankruptcyTrustee() {
      return this.userInfo.isBankruptcyTrustee;
    },
    isTrusteeApplying() {
      const currentIdentity = this.currentUser?.impersonatedUser?.socialId;
      if (!currentIdentity) return false;
      return (this.formModel.trustees || []).some(s => s.identityNumber === currentIdentity);
    },
    agent() {
      if (this.isDebtorRequestOwner) return {...this.formModel.requestApplicant.debtor, isIdentityVerified: true };
      if (this.isCreditor || this.isRequestOwnerCreditor) return {...this.formModel.requestApplicant.creditor, isIdentityVerified: true };
      if (this.isPartner) return {...this.formModel.requestApplicant.partner, isIdentityVerified: true };
      return {};
    },
    isStakeholder() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.stakeholder;
    },
    showFirmTable() {
      return this.formModel.requestApplicant?.requestOwnerTypeId 
            && !this.isStakeholder
            || this.isCreditor 
            || this.isPartner;
    },
    isObjectionRequest() {
      return this.subRequestType[this.$route.params.requestType] == subRequestType.VerdictObjectionRequest;
    },
    isObjectionOnSubRequest() {
      return this.formModel.parentSubRequestObjectKey && this.isObjectionRequest;
    },
    isCreditor() {
      return this.formModel.requestApplicant?.stakeholderTypeId == stakeholderTypesEnum.creditor;
    },
    isPartner() {
      return this.formModel.requestApplicant?.stakeholderTypeId == stakeholderTypesEnum.partner;
    },
    isBusiness() {
      return this.currentUser.loginType == "business"
    },
    showCreditorForm() {
      return this.isStakeHolderRequestOwner && this.isCreditor;
    },
    showPartnerForm() {
      return this.isStakeHolderRequestOwner && this.isPartner;
    },
    isDebtorRequestOwner() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.debtor;
    },
    isTrusteeRequestOwner() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.trustee;
    },
    isStakeHolderRequestOwner() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.stakeholder;
    },
    isOriginalPartyCreditor() {
      return this.partyInfo && this.partyInfo.legalCapacityId == legalCapacitiesEnum.creditor;
    },
    isOriginalPartyPartner() {
      return this.partyInfo && this.partyInfo.legalCapacityId == legalCapacitiesEnum.partner;
    },
  },
  watch: {},
  mounted() {
    this.getLegalCapacities();
    this.getAllRequestOwnerTypes();
    this.getStakeholderTypes();
    if (this.isNewMode)
      this.loadData();
  },
  methods: {
    ...mapActions([
      'getMainRequestOrganizationPartyInfo',
      'getLegalCapacities',
      'getMainRequestParties',
      'inquiryMainRequestPartner',
      'inquiryMainRequestCreditor',
      'getMainRequestSecretariesApproved',
      'getAllRequestOwnerTypes',
      'getStakeholderTypes',
    ]),
    loadData() {
      this.loading = true;
      Promise.all([
        this.getMainRequestParties(this.formModel.mainRequest.objectKey),
        this.getMainRequestOrganizationPartyInfo({
          objectKey: this.formModel.mainRequest.objectKey,
          unifiedNumber: this.userInfo.nationalRecordNumber,
          crNumber: this.userInfo.commercialRecordNumber,
        }),
        this.getMainRequestSecretariesApproved(this.formModel.mainRequest.objectKey),
      ])
        .then((res) => {
          this.requestParties = res[0].data;
          this.partyInfo = res[1].data;
          this.$set(this.formModel, 'trustees', res[2]?.data);
          this.setLegalCapacityId();
          if(this.isTrusteeApplying){
            this.setTrustee();
          }
          this.buildRequestApplicantDetails();
        })
        .finally(() => (this.loading = false));
    },
    stakeholderTypeChanged() {

      this.$set(this.formModel.requestApplicant, 'partner',  this.isPartner ? {} : null);
      this.$set(this.formModel.requestApplicant, 'creditor', this.isCreditor ? {} : null);
      if(this.isPartner){
          this.setPartnerData({
            typeId: partyTypeEnum.government,
            unifiedNumber: this.userInfo.nationalRecordNumber,
            creditorClassificationId: creditorClassificationsEnum.governmental,
          });
      }
      else if(this.isCreditor){
          this.setCreditorData({
            typeId: partyTypeEnum.government,
            unifiedNumber: this.userInfo.nationalRecordNumber,
            creditorClassificationId: creditorClassificationsEnum.governmental,
          });
      }
    },
    legalCapacityChanged(value) {
      this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', null);
      this.$set(this.formModel.requestApplicant, 'requestOwnerTypeId', null);
    },
    buildRequestApplicantDetails() {
      this.setApplicantInitialInfo();
      if(!this.isNewMode){
        this.setRequestOwnerType();
      }
      this.resetApplicantPartyRolesObjects();

      this.setDebtorData();
      if (this.isStakeHolderRequestOwner) {
        if (this.isOriginalPartyCreditor) {
          this.loadCreditorInfo();
        } else if (this.isOriginalPartyPartner) {
          this.loadPartnerInfo();
        }
      }
    },
    requestOwnerTypeChanged(value) {
      this.$set(this.formModel, 'originalParty', {});
      if (this.isDebtorRequestOwner) {
        this.$set(this.formModel.requestApplicant, 'bankruptId', this.formModel.requestApplicant.debtor.bankruptId);
        this.$set(this.formModel, 'originalParty', this.formModel.requestApplicant.debtor);
      }
      else {
        this.$set(this.formModel.requestApplicant, 'bankruptId', null);
      }
    },
    setApplicantInitialInfo() {
      const requestApplicantUserModel = {
        applicantName: [
          this.userInfo.impersonatedUser.firstName,
          this.userInfo.impersonatedUser.middleName,
          this.userInfo.impersonatedUser.thirdName,
          this.userInfo.impersonatedUser.familyName,
        ]
          .filter((n) => n)
          .join(' '),
        socialId: this.userInfo.impersonatedUser.socialId,
        socialIdType: this.userInfo.impersonatedUser.socialTypeId,
        nationality: this.userInfo.impersonatedUser.nationalityId,
        gender: this.userInfo.impersonatedUser.gender,
        dateOfBirth: this.userInfo.impersonatedUser.dateOfBirth,
        firstName: this.userInfo.impersonatedUser.firstName,
        fatherName: this.userInfo.impersonatedUser.middleName,
        grandFatherName: this.userInfo.impersonatedUser.thirdName,
        familyName: this.userInfo.impersonatedUser.familyName,
      };
      this.$set(this.formModel.requestApplicant, 'applicantName', requestApplicantUserModel.applicantName);
      this.$set(this.formModel.requestApplicant, 'fullName', requestApplicantUserModel.applicantName);
      this.$set(this.formModel.requestApplicant, 'identityTypeId', requestApplicantUserModel.socialIdType);
      this.$set(this.formModel.requestApplicant, 'identityNumber', requestApplicantUserModel.socialId);
      this.$set(this.formModel.requestApplicant, 'firstName', requestApplicantUserModel.firstName);
      this.$set(this.formModel.requestApplicant, 'fatherName', requestApplicantUserModel.fatherName);
      this.$set(this.formModel.requestApplicant, 'thirdName', requestApplicantUserModel.grandFatherName);
      this.$set(this.formModel.requestApplicant, 'familyName', requestApplicantUserModel.familyName);
      this.$set(this.formModel.requestApplicant, 'dateOfBirth', requestApplicantUserModel.dateOfBirth);
      this.$set(this.formModel.requestApplicant, 'nationality', requestApplicantUserModel.nationality);
      this.$set(this.formModel.requestApplicant, 'gender', requestApplicantUserModel.gender);
    },
    setRequestOwnerType() {
      this.$set(this.formModel.requestApplicant, 'requestOwnerTypeId', this.getRequestOwnerType());
    },
    resetApplicantPartyRolesObjects() {
      this.$set(this.formModel.requestApplicant, 'debtor', null);
      this.$set(this.formModel.requestApplicant, 'creditor', null);
      this.$set(this.formModel.requestApplicant, 'debtorEmployee', null);
      this.$set(this.formModel.requestApplicant, 'partner', null);
    },
    setDebtorData() {
      this.$set(this.formModel.requestApplicant, 'debtor', this.requestParties.debtor);
      this.$set(this.formModel.requestApplicant, 'bankruptId', this.requestParties.debtor.bankruptId);
    },
    setCreditorData(creditorObject) {
      this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.creditor);
      this.$set(this.formModel.requestApplicant, 'creditor', creditorObject);
    },
    setLegalCapacityId() {
      this.$set(
        this.formModel.requestApplicant,
        'legalCapacityId',
        this.isOrganizationBankruptcyTrustee || this.isTrusteeApplying
          ? legalCapacitiesEnum.bankruptcySecretary
          : this.isBusiness ? legalCapacitiesEnum.legalEntityRepresentative : legalCapacitiesEnum.administrativeEntityRepresentative,
      );
    },
    setTrustee() {
      const currentIdentity = this.currentUser?.impersonatedUser?.socialId;
      const selectedTrustee = this.formModel.trustees.find(s => s.identityNumber === currentIdentity);      
      this.$set(this.formModel.requestApplicant, 'trustee', selectedTrustee);
    },
    loadCreditorInfo() {
      this.loading = true;
      const payload = {
        objectKey: this.formModel.mainRequest.objectKey,
        commercialRegistrationNumber: this.userInfo.commercialRecordNumber,
        unifiedNumber: this.userInfo.nationalRecordNumber,
      };
      return this.inquiryMainRequestCreditor(payload)
        .then((res) => {
          if (res.data) {
            res.data.isRetrieved = true;
            this.setCreditorData(res.data);
          }
        })
        .finally(() => (this.loading = false));
    },
    getRequestOwnerType() {
      if (this.requestParties.requestOwnerTypeId) return this.requestParties.requestOwnerTypeId;

      if (this.userInfo.isBankruptcyTrustee) return requestOwnerTypeEnum.bankruptcyCommission;

      if (
        this.partyInfo.legalCapacityId == legalCapacitiesEnum.debtor ||
        this.partyInfo.legalCapacityId == legalCapacitiesEnum.competentParty ||
        this.partyInfo.legalCapacityId == legalCapacitiesEnum.bankrupt
      )
        return requestOwnerTypeEnum.debtor;

      if (
        this.partyInfo.legalCapacityId == legalCapacitiesEnum.partner ||
        this.partyInfo.legalCapacityId == legalCapacitiesEnum.creditor
      )
        return requestOwnerTypeEnum.stakeholder;

      if (!this.partyInfo || !this.partyInfo.legalCapacityId) {
        return requestOwnerTypeEnum.stakeholder;
      }
    },
    loadPartnerInfo() {
      this.loading = true;
      const payload = {
        objectKey: this.formModel.mainRequest.objectKey,
        commercialRegistrationNumber: this.userInfo.commercialRecordNumber,
        unifiedNumber: this.userInfo.nationalRecordNumber,
      };
      this.inquiryMainRequestPartner(payload)
        .then((res) => {
          if (res.data) {
            this.setPartnerData(res.data);
            res.data.isRetrieved = true;
          }
        })
        .finally(() => (this.loading = false));
    },
    setPartnerData(partnerObject) {
      this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.partner);
      this.$set(this.formModel.requestApplicant, 'partner', partnerObject);
    },
  },
};
</script>

<style lang="scss" scoped></style>
